import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, CheckCircle, ChevronDown, ChevronUp, X, ArrowRight, Stethoscope, BadgeDollarSign } from 'lucide-react';
import '../styles/Founders.css';
import Footer from '../components/Footer';
import UniversalCTA from '../components/UniversalCTA';
import SEO from '../components/SEO';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';

// PAGE 3: FOUNDERS + LEADERSHIP + ABOUT CADENCE
// 3 Parts: 1. Meet the Founders, 2. Our Leadership, 3. About CADENCE

const About = () => {
  const [expandedFounders, setExpandedFounders] = useState({});
  const [expandedImage, setExpandedImage] = useState(null);
  // Tap-photo-reveals-quote: track which founder's personal quote is
  // currently revealed below their photo. Single-open at a time.
  const [revealedQuote, setRevealedQuote] = useState(null);
  const pageRef = useRef(null);

  // Curtain pages render side-by-side in a single horizontal scroll
  // container and never unmount. If the user opens the photo modal then
  // swipes/taps to the next curtain page, the modal would persist as a
  // global fixed overlay covering subsequent pages (verified bug
  // 2026-05-26). Close the modal whenever the curtain container scrolls
  // past the bounds of this Founders panel.
  useEffect(() => {
    if (!expandedImage) return;
    const scroller = document.querySelector('.curtain-scroll-container');
    if (!scroller) return;

    let closed = false;
    const onScroll = () => {
      if (closed || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      // If the Founders panel has scrolled more than 50% out of viewport
      // horizontally (either left or right), close the modal.
      const vw = window.innerWidth;
      const visibleW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
      if (visibleW / vw < 0.5) {
        closed = true;
        setExpandedImage(null);
      }
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [expandedImage]);

  // Also close the modal on Escape — keyboard users get a clean exit.
  useEffect(() => {
    if (!expandedImage) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpandedImage(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedImage]);

  // Lock body scroll while the photo modal is open so the page underneath
  // doesn't scroll, AND so the global AdminPreviewBar hide-on-modal CSS
  // rule (body[style*="overflow: hidden"]) kicks in — otherwise the admin
  // bar's invisible 12px top-edge trigger blocks the modal close button.
  useEffect(() => {
    if (!expandedImage) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [expandedImage]);

  const toggleFounder = (index) => {
    setExpandedFounders(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const founders = [
    {
      // Anchor id for in-site Person `sameAs` linking (knowledge-panel
      // routing). Matches the @id used in the global Person JSON-LD.
      slug: 'lakia-stewart',
      name: 'Lakia S. Stewart, DNP, FNP-C, WHNP-BC',
      credentials: 'DNP, FNP-C, WHNP-BC',
      title: 'Chief Medical Officer & Co-Founder',
      tagline: 'Women\'s Health Expert\nMedical Fiduciary',
      mission: 'Empowering patients through clarity & advocacy.',
      image: '/lakia-headshot.png',
      shortBio: 'Dr. Stewart is a Doctor of Nursing Practice with dual board certifications in Family and Women\'s Health, and 20+ years of distinguished military medical service.',
      fullBio: 'She holds full prescriptive authority NP licensure in Maryland and Delaware, and maintains multi-state RN licensure in Florida and Texas. Her military background instilled the precision and dedication that defines CADENCE\'s approach to patient care. She leads all clinical analysis and quality assurance.',
      personalQuote: '"I\'ve seen what happens when complex conditions get simplified into standard protocols. At CADENCE, we take the time to understand your\u00A0unique\u00A0story."',
      specializations: [
        'Family Health Medicine',
        'Women\'s Health',
        'Military Medicine',
        'Clinical Leadership',
        'Patient Advocacy & Health Equity'
      ],
      certifications: [
        'Family Nurse Practitioner (FNP-C)',
        'Women\'s Health Nurse Practitioner (WHNP-BC)',
        'Doctor of Nursing Practice (DNP)',
        'NP Licensure with Full Prescriptive Authority (MD, DE)',
        'RN Multi-State Licensure (FL, TX)'
      ]
    },
    {
      slug: 'andera-peeples',
      name: 'Andera N. Peeples, MS, DEL Candidate',
      credentials: 'MS, DEL Candidate',
      title: 'Chief Executive Officer & Co-Founder',
      tagline: 'Healthcare Systems Architect | Medical Equity Expert',
      mission: 'On a mission to build a more human-centered healthcare model.',
      image: '/founder-leadership.png',
      shortBio: 'Andera is a healthcare systems architect and doctoral candidate in Executive Leadership at the University of Lynchburg. Her research focuses on the lived experience of endometriosis patients\u2014specifically, the systemic leadership failures that contribute to an average diagnostic delay of 7 to 10 years. She holds dual Master of Science degrees in Healthcare Administration and Disaster Preparedness and has served 16 years as an Army Respiratory Technician (68V).',
      fullBio: 'Andera\'s unique perspective bridges executive leadership and patient experience. She understands that complex diagnoses are not just clinical puzzles\u2014they are systemic failures that require executive-level solutions. At CADENCE, she ensures that every client interaction is informed by both operational discipline and a deep understanding of the patient\'s lived experience.',
      personalQuote: '"We\'re building a healthcare experience where the system serves the patient\u2014not the other way around."',
      specializations: [
        'Healthcare Systems Architecture',
        'Health Equity & Endometriosis Research',
        'Disaster Preparedness',
        'Respiratory Medicine',
        'Strategic Leadership'
      ],
      certifications: [
        'Doctoral Candidate, Executive Leadership (University of Lynchburg)',
        'MS, Healthcare Administration',
        'MS, Disaster Preparedness',
        'Army Respiratory Technician (68V)'
      ]
    }
  ];

  const ImageModal = ({ src, alt, video, onClose, showFoundersQuote }) => (
    <div className="image-modal-overlay" onClick={onClose}>
      <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="image-modal-close" onClick={onClose} data-testid="founders-modal-close">
          <X size={24} />
        </button>
        {video ? (
          <video
            src={video}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="founders-modal-video"
            aria-label={alt}
            data-testid="founders-modal-video"
          >
            Your browser does not support embedded video.
          </video>
        ) : (
          <img src={src} alt={alt} />
        )}
        {showFoundersQuote && (
          <div className="founders-modal-quote">
            <p className="modal-quote-text">"Clinical excellence meets business innovation. We're not just another medical practice — we're your partners in navigating healthcare with&nbsp;confidence."</p>
            <p className="modal-quote-attribution">— Andera and Lakia</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="about-page" ref={pageRef}>
      <SEO
        title="Our Founders — Lakia Stewart, DNP & Andera Peeples, MSDP"
        description="Meet Dr. Lakia S. Stewart and Andera N. Peeples — the U.S. military medical veterans who founded CADENCE. 19+ years in nursing practice, 14 years in healthcare operations."
        path="/founders"
      />
      {expandedImage && (
        <ImageModal 
          src={expandedImage.src} 
          alt={expandedImage.alt} 
          video={expandedImage.video}
          onClose={() => setExpandedImage(null)}
          showFoundersQuote={expandedImage.showQuote}
        />
      )}

      {/* Hidden gradient defs — same engraved gold treatment as the
          AboutUs heraldic mission pillars + pie chart. Used by the
          Specializations and Credentials list icons below. */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="founders-engraved-gold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F5D86E" />
            <stop offset="48%" stopColor="#C9A227" />
            <stop offset="100%" stopColor="#5C2E0E" />
          </linearGradient>
        </defs>
      </svg>

      {/* ==========================================
          PART 1: MEET THE FOUNDERS
          ========================================== */}
      
      {/* 0. LARGE VELVET EMBLEM - TOP OF PAGE */}
      <section className="emblem-section" data-testid="about-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img 
              src="/cadence-gold-foil.png" 
              alt="CADENCE Medical Consulting" 
              className="velvet-emblem-home" 
            />
          </div>
        </div>
      </section>

      {/* 1. MEET THE FOUNDERS - Title */}
      <section className="unified-content-section" data-testid="founders-title">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">Meet the Founders</h1>
        </div>
      </section>

      {/* 2. FOUNDERS QUOTE - FREE WHITE TEXT on velvet (no tile) */}
      <section className="unified-quote-section" data-testid="founders-quote-section">
        <div className="container">
          <blockquote className="unified-page-quote">
            "We created CADENCE because we refused to accept a healthcare system that leaves people behind&mdash;we're fighting for&nbsp;you."
          </blockquote>
          <p className="founders-quote-attribution">— Andera and Lakia, Founders of CADENCE</p>
        </div>
      </section>

      {/* 3. FOUNDERS PHOTO */}
      <section className="founders-photo-section" data-testid="founders-photo-section">
        <div className="container">
          <div 
            className="founders-photo-feathered"
            onClick={() => setExpandedImage({ 
              video: '/local-images/founders-quote-video.mp4',
              alt: 'Andera and Lakia - CADENCE Founders quote video',
              showQuote: false,
            })}
            data-testid="founders-photo-main"
          >
            <img 
              src="/local-images/founders-duo.jpg" 
              alt="Andera and Lakia - CADENCE Founders" 
              className="founders-feathered-img" 
              onError={(e) => {
                // Network/CDN failure → drop in the engraved CADENCE
                // emblem so the page never shows a broken-image marker.
                if (e.currentTarget.dataset.fallback === '1') return;
                e.currentTarget.dataset.fallback = '1';
                e.currentTarget.src = '/cadence-gold-foil.png';
                e.currentTarget.classList.add('img-fallback-emblem');
              }}
            />
            <span className="click-hint">Tap to hear from us</span>
          </div>
          {/* "Read Our Story" link beneath the duo photo — restored
              per user 2026-02 because the standalone tile was missed
              after removal. Drops the visitor into /our-story so they
              can read the longer narrative before meeting each founder
              individually below. */}
          <div className="founders-photo-cta" data-testid="founders-read-story-wrap">
            <Link
              to="/our-story"
              className="founders-read-story-link"
              data-testid="founders-read-our-story"
            >
              Read Our Story
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. VETERAN ADVANTAGE / 5. READ OUR STORY tiles removed
          2026-05-19 per user. Veteran detail lives inside each founder's
          expanded credentials; the "Our Story" link is one swipe over in
          the curtain navigation, so the standalone tile was redundant. */}

      {/* WHY CADENCE — relocated from /home (2026-02) so trust marks
          sit immediately above the leadership intro, where credibility
          context lands hardest. Uses the same engraved-gold trio
          treatment that was on the home page. */}
      <section className="why-cadence-section" data-testid="why-cadence">
        <div className="container">
          <h2 className="why-cadence-title">Why CADENCE</h2>

          {/* Hidden gradient defs — gives the trio icons the same shimmery
              engraved-gold stroke that the Promise pie-slice icons use. */}
          <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden="true">
            <defs>
              <linearGradient id="founders-engraved-gold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFF0B5" />
                <stop offset="35%" stopColor="#F5D86E" />
                <stop offset="65%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#7C5224" />
              </linearGradient>
            </defs>
          </svg>

          <div className="why-cadence-grid">
            <div className="why-cadence-item" data-testid="why-veteran">
              <div className="why-cadence-icon-wrap">
                <Shield className="why-cadence-icon" strokeWidth={1.75} stroke="url(#founders-engraved-gold)" />
              </div>
              <h3 className="why-cadence-heading">Veteran-Owned</h3>
              <p className="why-cadence-copy">
                Founded by two military medical veterans who refused to let one more patient leave unheard.
              </p>
            </div>
            <div className="why-cadence-item" data-testid="why-experience">
              <div className="why-cadence-icon-wrap">
                <Stethoscope className="why-cadence-icon" strokeWidth={1.75} stroke="url(#founders-engraved-gold)" />
              </div>
              <h3 className="why-cadence-heading">36+ Years Combined</h3>
              <p className="why-cadence-copy">
                Of frontline military medical experience &mdash; precision, patience, and full-picture care.
              </p>
            </div>
            <div className="why-cadence-item" data-testid="why-cashpay">
              <div className="why-cadence-icon-wrap">
                <BadgeDollarSign className="why-cadence-icon" strokeWidth={1.75} stroke="url(#founders-engraved-gold)" />
              </div>
              <h3 className="why-cadence-heading">Cash-Pay by Design</h3>
              <p className="why-cadence-copy">
                Insurance does not dictate what we review. You do. Every page gets read.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================
          PART 2: OUR LEADERSHIP - REMOVED "ABOUT CADENCE" section
          Moved to ServicesPage (Page 4)
          ========================================== */}
      
      <section className="leadership-section" data-testid="leadership-section">
        <div className="container">
          <h2 className="section-title-white">Our Leadership</h2>
          
          <div className="founders-grid">
            {founders.map((founder, index) => (
              <div
                key={index}
                id={founder.slug}
                className={`founder-card-solid ${expandedFounders[index] ? 'is-expanded' : ''}`}
                data-testid={`founder-card-${index}`}
              >
                {/* PHOTO — when expanded, lays out in a 3-col grid with
                    Specializations on LEFT and Credentials on RIGHT. */}
                <div className="founder-photo-row">
                  {/* LEFT column — Specializations (only when expanded) */}
                  {expandedFounders[index] && (
                    <div className="founder-side-col founder-side-col--left">
                      <h4 className="detail-heading">Specializations</h4>
                      <ul className="detail-list">
                        {founder.specializations.map((spec, idx) => (
                          <li key={idx}>
                            <span className="detail-list-sigil" aria-hidden="true">
                              <CheckCircle size={18} strokeWidth={2.25} />
                            </span>
                            <span>{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CENTER — the founder photo. Tap-to-reveal pattern
                      retired now that both quotes render by default. */}
                  <div className="founder-photo-top">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="founder-avatar-wide"
                      data-testid={`founder-photo-${index}`}
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallback === '1') return;
                        e.currentTarget.dataset.fallback = '1';
                        e.currentTarget.src = '/cadence-gold-foil.png';
                        e.currentTarget.classList.add('img-fallback-emblem');
                      }}
                    />
                  </div>

                  {/* RIGHT column — Credentials (only when expanded) */}
                  {expandedFounders[index] && (
                    <div className="founder-side-col founder-side-col--right">
                      <h4 className="detail-heading">Credentials</h4>
                      <ul className="detail-list">
                        {founder.certifications.map((cert, idx) => (
                          <li key={idx}>
                            <span className="detail-list-sigil" aria-hidden="true">
                              <Award size={18} strokeWidth={2.25} />
                            </span>
                            <span>{cert}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* "Meet your advocate" expand button — relocated 2026-02
                    to sit immediately under the photo so the credentials
                    that open above (Specializations / Credentials) are
                    instantly visible without scrolling back up. Previously
                    lived at the bottom of the card, forcing a scroll loop. */}
                <button
                  className="expand-btn expand-btn--under-photo"
                  onClick={() => toggleFounder(index)}
                  data-testid={`expand-founder-${index}`}
                >
                  {expandedFounders[index] ? (
                    <>View Less <ChevronUp size={18} /></>
                  ) : (
                    <>Meet your advocate <ChevronDown size={18} /></>
                  )}
                </button>

                {/* Golden quote — always visible beneath each founder's
                    photo so both quotes are readable at once. Previously
                    gated behind a tap-photo-to-reveal interaction which
                    obscured the founders' voices and required two taps
                    to compare quotes. */}
                {founder.personalQuote && (
                  <blockquote
                    className="founder-tap-quote founder-tap-quote--always-visible"
                    data-testid={`founder-tap-quote-${index}`}
                  >
                    {founder.personalQuote}
                  </blockquote>
                )}

                {/* NAME & DESCRIPTION BELOW PHOTO */}
                <div className="founder-info-below">
                  <h3 className="founder-name-white">{founder.name}</h3>
                  <p className="founder-title-role">{founder.title}</p>
                  <p className="founder-tagline" style={{ whiteSpace: 'pre-line' }}>{founder.tagline}</p>
                  <p className="founder-mission">{founder.mission}</p>
                </div>

                <p className="founder-short-bio">{founder.shortBio}</p>

                {expandedFounders[index] && (
                  <div className="founder-expanded">
                    <p className="founder-full-bio">{founder.fullBio}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COPPER TILE - "You are not alone" - BEFORE swipe indicator */}
      <section className="founders-copper-tile-section" data-testid="founders-copper-tile">
        <div className="container">
          <div className="founders-copper-tile">
            <p className="founders-copper-text">You are not alone. We are here to ensure you are seen, heard, and empowered.</p>
          </div>
        </div>
      </section>

      {/* SWIPE INDICATOR - Clickable text link (no box) */}
      <section className="swipe-indicator-section" data-testid="swipe-indicator">
        <div className="container">
          <Link 
            to="/cadence-promise"
            className="swipe-hint-text"
            data-testid="swipe-to-services"
          >
            Learn more about CADENCE →
          </Link>
        </div>
      </section>

      <UniversalCTA />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;
